import * as React from 'react';
import styled, { css, keyframes } from 'styled-components';
import { ButtonStyle } from '../../../components_v2/popup/PopupCreation';
import AiSparklingsIcon from '../../custom-icons/ai-sparklings';
import { DefaultText } from './GlobalText';
import {
	AiGradiant,
	PrimaryColor,
	BorderColor2,
	DarkGreySidely2,
	RedSidely,
	SidelyBlack,
	SurfaceColor,
	OnPrimaryColor
} from './Utils';

interface DefaultButtonProps {
	error?: boolean
	width?: string
	minWidth?: string
	height?: string
	margin?: string
	padding?: string
	lineHeight?: string
	buttonStyle?: ButtonStyle
	borderSize?: string
	textWrap?: string,
}

export const DEFAULT_BUTTON_HEIGHT = 28;

const DefaultButton = styled.button<DefaultButtonProps>`
    transition: all 0.25s ease-in-out;

    background-color: ${p => p.error ? RedSidely : SidelyBlack};
    filter: brightness(1);
    cursor: pointer !important;
    border-radius: 8px;
    border-style: none;
    ${p => p.width ? `width: ${p.width};` : ''}
    ${p => p.minWidth ? `min-width: ${p.minWidth};` : ''}
    height: ${p => p.height ? p.height : DEFAULT_BUTTON_HEIGHT + 'px'};
    padding: ${p => p.padding ?? '0.25em 1em'};
    color: white;
    font-family: 'Inter', sans-serif;
    font-style: normal;
    font-weight: 500;
    font-size: 12px;
    ${p => p.textWrap ? `text-wrap: ${p.textWrap};` : ''}
    line-height: ${p => p.lineHeight ?? '20px'};
    text-align: center;
    letter-spacing: 0.01em;
    margin: ${p => p.margin ?? '10px'};
    ${p => {
		if (!p.buttonStyle) return '';
		switch (p.buttonStyle) {
			case 1: return `
		    background-color: ${RedSidely};
		`;
			case 2: return `
            padding: calc(0.25em - 1px) 1em;
            background-color: ${SurfaceColor};
		    color: ${PrimaryColor};
		    border: ${p.borderSize ?? '2px'} solid ${PrimaryColor};
		`;
			case 3: return `
            background-color: ${PrimaryColor};
            color: ${OnPrimaryColor};
			`;
			case 4: return `
            background: ${AiGradiant};
            color: white;
            `;
			default: return '';
		}
	}}

    &:hover:enabled {
        filter: brightness(1.1);
    }

    &:disabled {

    ${p => {
		switch (p.buttonStyle) {
			case 2: return `
            padding: calc(0.25em - 1px) 1em;
		    color: ${BorderColor2};
		    border: ${p.borderSize ?? '2px'} solid ${BorderColor2};
            cursor: not-allowed !important;
            box-shadow: none;
		`;
			default: return `
            background: ${BorderColor2};
            cursor: not-allowed !important;
            box-shadow: none;
        `;
		}
	}}
    }
`;

const spinAnimation = keyframes`
  100% { transform: rotate(360deg); }
`;
const blinkAnimation = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0; }
`;

export const AiAnimation = css`
	#ai-main-star {
		transition: none;
		transform-origin: 11.2708px 11.9809px;
	}

  &:hover #ai-main-star {
    rotate: 360deg;
    transition: rotate 1s ease-in-out;
    transform-origin: 11.2708px 11.9809px;
  }

  &:hover #ai-sec-star {
    animation: ${spinAnimation} cubic-bezier(0.42,0.00,0.58,1.00) reverse 1s, ${blinkAnimation} cubic-bezier(0.42,0.00,0.58,1.00) 1s;
    transform-origin: center;
  }
	`;

const AiButtonDiv = styled(DefaultButton)`
	${AiAnimation}
`;

export function AiButton(props: { innerStyle?: React.CSSProperties } & typeof DefaultButton.defaultProps) {
	// @ts-expect-error it works
	return <AiButtonDiv
		buttonStyle={4}
		style={{ display: 'flex', alignItems: 'center', gap: '0.5ch', ...(props?.innerStyle ?? {}) }}
		{...props}
	>
		<AiSparklingsIcon color='white' style={{ marginLeft: '-0.5ch' }} />
		{props?.children}
	</AiButtonDiv>;
}

const DefaultCloseButton = styled.span`
    ${DefaultText}
    transition: color 0.4s ease;
    font-size: 20px;
    color: ${DarkGreySidely2};
    cursor: pointer;
    border: none;
    background-color: transparent;
    font-weight: 700;
    line-height: 1;

    &:after {
        content: 'x';
    }

    &:hover {
        color: ${SidelyBlack};
    }
`;

const DefaultSecondaryButton = styled(DefaultButton)`
    background-color: ${SurfaceColor};
    color: ${PrimaryColor};
    border: 2px solid ${PrimaryColor};
`;

export {
	DefaultButton,
	DefaultCloseButton,
	DefaultSecondaryButton
};
